import { useState, useEffect, useCallback, useMemo } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useNavigate, useParams } from "react-router";
import Form from "../common/Form";
import { useSelector } from "react-redux";
import APIManager from "../../managers/APIManager";
import Loading from "../common/Loading";
import Button from "../common/Button";
import { EditableNewsItem, SortableNewsItem } from "./NewsItem";

const AIVideoEditor = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { id: videoCreationId } = params;

  const [loading, setLoading] = useState(true);
  const [newsList, setNewsList] = useState([]);
  const [selectedNews, setSelectedNews] = useState([]);
  const [orderedNews, setOrderedNews] = useState([]);

  const [videoTitle, setVideoTitle] = useState("");
  const [videoDescription, setVideoDescription] = useState("");
  const [videoTemplateName, setVideoTemplateName] = useState("news1");
  const [videoFields, setVideoFields] = useState({});
  const [videoTemplates, setVideoTemplates] = useState([]);
  const [videoTemplate, setVideoTemplate] = useState(null);
  const videoRestrictions = useMemo(
    () => videoTemplate?.template?.restrictions || [],
    [videoTemplate]
  );
  const VIDEO_TEMPLATE_ITEM_COUNT = useMemo(
    () => videoTemplate?.template?.content?.news?.length || 0,
    [videoTemplate]
  );

  const siteVirgulId = useSelector(
    (state) => state.site?.selectedSite?.virgulId
  );
  const siteId = useSelector((state) => state.site?.selectedSite?.id);
  const videoCreation = useSelector(
    (state) => state.site?.selectedSite?.videoCreation
  );

  const saveVideo = useCallback(
    async (immediateSave = false) => {
      if (!orderedNews.length === VIDEO_TEMPLATE_ITEM_COUNT) {
        return;
      }
      setLoading(true);
      const transformedNews = orderedNews.map((news) => ({
        title: news.spotTitle,
        description: news.title,
        image: news.image,
        voiceText: news.voiceText,
        category: news.category,
      }));
      try {
        await APIManager.saveAIVideo({
          siteId: siteVirgulId,
          creationId: videoCreationId,
          title: videoTitle,
          description: videoDescription,
          news: transformedNews,
          immediateSave,
          templateName: videoTemplateName,
          content: videoFields,
        });
        setSelectedNews([]);
        setOrderedNews([]);
        setVideoTitle("");
        navigate(`/ai-video`);
      } catch (error) {
        console.error("Error generating video:", error);
      } finally {
        setLoading(false);
      }
    },
    [
      orderedNews,
      siteVirgulId,
      videoCreationId,
      videoTitle,
      videoDescription,
      videoFields,
      videoTemplateName,
    ]
  );

  const fetchNews = useCallback(async () => {
    if (!videoCreation || !siteId || !videoCreationId) {
      setLoading(false);
      navigate("/");
      return;
    }

    setLoading(true);
    try {
      let currentNewsList = [];
      if (videoCreationId !== "new") {
        const res = await APIManager.getAIVideo({
          siteId: siteVirgulId,
          creationId: videoCreationId,
        });

        setVideoTitle(res?.data?.title);
        setVideoDescription(res?.data?.description);
        setVideoFields(res?.data?.content || {});
        setVideoTemplateName(res?.data?.templateName);

        if (res?.data?.content?.news && Array.isArray(res.data.content.news)) {
          const oldNewsItems = res.data.content.news
            .filter((item) => item.title && item.title.trim() !== "")
            .map((item, index) => ({
              ...item,
              id: `ai-${Date.now()}-${index}`,
              spotTitle: item.title,
              title: item.description,
              voiceText: item.voiceText,
              image: item.image,
            }));

          // Add AI news items to the beginning of the news list
          setNewsList(oldNewsItems);

          // Auto-select all AI-generated news items
          const aiNewsIds = oldNewsItems.map((item) => item.id);
          setSelectedNews(() => aiNewsIds);
          setOrderedNews(oldNewsItems);
          currentNewsList = oldNewsItems;
        }
      }
      const fetchedSiteNewsRes = await APIManager.getFetchedSiteNews(siteId);

      const existingTitles = new Set(currentNewsList.map((item) => item.title));

      // Filter out site news items with duplicate titles
      const uniqueSiteNews = (fetchedSiteNewsRes?.data || [])
        .filter((item) => !existingTitles.has(item.title))
        .map((item, index) => ({
          ...item,
          id: index + 1,
        }));

      // Merge AI news with unique site news
      setNewsList((prevList) => {
        return [...prevList, ...uniqueSiteNews];
      });
    } catch (error) {
      console.error("Error fetching news:", error);
      setNewsList([]);
    } finally {
      setLoading(false);
    }
  }, [videoCreation, siteId, videoCreationId, navigate, siteVirgulId]);

  const fetchVideoTemplates = useCallback(async () => {
    const res = await APIManager.getAIVideoTemplates({ siteId: siteVirgulId });
    setVideoTemplates(res?.data || []);
  }, [siteVirgulId]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fixed checkbox logic - only toggle the specific item
  const handleToggleSelect = (newsId) => {
    setSelectedNews((prev) => {
      if (prev.includes(newsId)) {
        // Remove from selected and ordered list
        setOrderedNews((prevOrdered) =>
          prevOrdered.filter((news) => news.id !== newsId)
        );
        return prev.filter((id) => id !== newsId);
      } else {
        // Add to selected and ordered list
        const newsItem = newsList.find((news) => news.id === newsId);
        if (newsItem && !orderedNews.some((ordered) => ordered.id === newsId)) {
          setOrderedNews((prevOrdered) => [...prevOrdered, newsItem]);
        }
        return [...prev, newsId];
      }
    });
  };

  const handleEdit = (newsId, field, value) => {
    // Update both newsList and orderedNews to keep them in sync
    setNewsList((prev) =>
      prev.map((news) =>
        news.id === newsId ? { ...news, [field]: value } : news
      )
    );

    setOrderedNews((prev) =>
      prev.map((news) =>
        news.id === newsId ? { ...news, [field]: value } : news
      )
    );
  };

  const handleRemoveFromOrdered = (newsId) => {
    setOrderedNews((prev) => prev.filter((news) => news.id !== newsId));
    // Also uncheck the item
    setSelectedNews((prev) => prev.filter((id) => id !== newsId));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setOrderedNews((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };
  const handleAddEmptyNewsItem = () => {
    setNewsList((prev) => [
      {
        id: Date.now(),
        title: "",
        description: "",
        voiceText: "",
        image: "",
      },
      ...prev,
    ]);
  };

  useEffect(() => {
    fetchNews();
    fetchVideoTemplates();
  }, [fetchNews, fetchVideoTemplates]);

  useEffect(() => {
    if (videoTemplateName && videoTemplates.length > 0) {
      setVideoTemplate(
        videoTemplates.find((template) => template.name === videoTemplateName)
      );
    }
  }, [videoTemplateName, videoTemplates]);

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Left Side - News List (Non-sortable, editing only) */}
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">
              News Management
            </h1>
            <div className="text-sm text-gray-600">
              Check items to automatically add them to playlist
            </div>
          </div>

          <div className="mb-6">
            <Form.FormTextInput
              name={`videoTitle`}
              value={videoTitle}
              onChange={(e) => setVideoTitle(e.target.value)}
              className="text-sm font-semibold text-gray-900 bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
              placeholder="Video title"
            />
          </div>
          <div className="mb-6">
            <Form.Textarea
              name={`videoDescription`}
              value={videoDescription}
              onChange={(e) => setVideoDescription(e.target.value)}
              className="text-sm font-semibold text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
              placeholder="Video description"
            />
          </div>
          <div className="mb-6">
            <select
              name="videoTemplate"
              value={videoTemplateName}
              onChange={(e) => setVideoTemplateName(e.target.value)}
              disabled={
                VIDEO_TEMPLATE_ITEM_COUNT <= 1 || videoCreationId !== "new"
              }
              className={`text-sm font-semibold text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1 w-full border border-gray-300 ${
                VIDEO_TEMPLATE_ITEM_COUNT <= 1
                  ? "cursor-not-allowed"
                  : "cursor-pointer"
              }`}
            >
              {VIDEO_TEMPLATE_ITEM_COUNT > 0 &&
                videoTemplates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
            </select>
          </div>
          <div className="mb-6">
            {Object.keys(videoRestrictions).map(
              (key) =>
                !key.startsWith("news[].") && (
                  <div className="flex-1" key={key}>
                    <Form.FormTextInput
                      name={key}
                      value={videoFields[key]}
                      onChange={(e) =>
                        setVideoFields({
                          ...videoFields,
                          [key]: e.target.value,
                        })
                      }
                      maxLength={videoRestrictions[key]?.maxLength}
                      className="text-sm font-semibold text-gray-900 bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
                      placeholder={key.toUpperCase()}
                      label={key.toUpperCase()}
                      error={
                        videoFields[key]?.length >
                        videoRestrictions[key]?.maxLength
                          ? `${key.toUpperCase()} is too long`
                          : ""
                      }
                    />
                    <div className="text-xs text-gray-500 mt-1">
                      {videoFields[key]?.length || 0} /{" "}
                      {videoRestrictions[key]?.maxLength}
                    </div>
                  </div>
                )
            )}
          </div>

          <div className="space-y-4">
            <div className="mb-4">
              <Button
                color="primary"
                className="w-full"
                onClick={handleAddEmptyNewsItem}
                type="button"
              >
                Add New
              </Button>
            </div>
            {newsList.map((news) => (
              <EditableNewsItem
                key={news.id}
                news={news}
                videoRestrictions={videoRestrictions}
                isSelected={selectedNews.includes(news.id)}
                onToggleSelect={handleToggleSelect}
                onEdit={handleEdit}
                isCheckboxDisabled={
                  orderedNews.length === VIDEO_TEMPLATE_ITEM_COUNT
                }
              />
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Ordered Playlist (Sortable + Preview) */}
      <div className="bg-white border-l border-gray-200 p-6 overflow-y-auto">
        <div className="sticky top-0 bg-white pb-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Playlist Order
          </h2>
          <p className="text-sm text-gray-600">
            Drag and drop to reorder news items
          </p>
        </div>

        {orderedNews.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📰</div>
            <p className="text-gray-500">No news items in playlist</p>
            <p className="text-sm text-gray-400 mt-2">
              Check news items from the left to add them to your playlist
            </p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={orderedNews.map((news) => news.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-3 mt-4">
                {orderedNews.map((news) => (
                  <div key={news.id} className="relative">
                    <SortableNewsItem
                      news={news}
                      onRemove={() => handleRemoveFromOrdered(news.id)}
                    />
                  </div>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        {orderedNews.length > 0 && (
          <div className="mt-6 pt-4 border-t border-gray-200 gap-2 flex flex-col">
            <div
              className={`text-sm text-gray-600 ${
                orderedNews.length === VIDEO_TEMPLATE_ITEM_COUNT
                  ? "text-green-500"
                  : "text-red-500"
              }`}
            >
              {orderedNews.length} / {VIDEO_TEMPLATE_ITEM_COUNT} items in
              playlist
            </div>
            <div className="flex justify-between items-center">
              <div className="flex gap-2">
                <Button
                  color="danger"
                  onClick={() => {
                    setOrderedNews([]);
                    setSelectedNews([]);
                  }}
                  className="text-xs px-3 py-1"
                >
                  Clear All
                </Button>
                <Button
                  color="inverse_primary"
                  onClick={() => saveVideo(false)}
                  disabled={
                    loading || orderedNews.length !== VIDEO_TEMPLATE_ITEM_COUNT
                  }
                  loading={loading}
                  className="text-xs px-3 py-1"
                >
                  Save Draft
                </Button>
                <Button
                  color="inverse_primary"
                  onClick={() => saveVideo(true)}
                  disabled={
                    loading ||
                    orderedNews.length !== VIDEO_TEMPLATE_ITEM_COUNT ||
                    videoCreationId === "new"
                  }
                  loading={loading}
                  className="text-xs px-3 py-1"
                >
                  Save for Generating
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIVideoEditor;
